<script setup>
import { ref, computed } from "vue";
import { useRoute } from "vue-router";
import { useAuthStore } from "@/stores/auth";
// import { MENU_ITEMS } from "@/config/permissions";
import { getMenuItems } from "@/config/permissions";

const route = useRoute();
const auth = useAuthStore();

const mobileExpanded = ref(false);

const roleLabelMap = {
  admin: "ผู้ดูแลระบบ",
  finance: "เจ้าหน้าที่การเงิน",
  user: "ลูกบ้าน",
};

// const visibleMenuItems = computed(() =>
//   MENU_ITEMS.filter((item) => item.roles.includes(auth.user?.role)),
// );

const visibleMenuItems = computed(() =>
  getMenuItems(auth).filter((item) => item.roles.includes(auth.user?.role)),
);


const toggleMobile = () => {
  mobileExpanded.value = !mobileExpanded.value;
};

// ปิด sidebar เมื่อคลิกเมนู (บน mobile)
const handleMenuClick = () => {
  mobileExpanded.value = false;
};
</script>

<template>
  <!-- Overlay เมื่อ sidebar ขยายบน mobile -->
  <div
    v-if="mobileExpanded"
    class="sidebar-overlay d-lg-none"
    @click="mobileExpanded = false"
  />

  <aside
    class="sidebar text-white d-flex flex-column"
    :class="{ 'mobile-expanded': mobileExpanded }"
  >
    <!-- Brand + Toggle Button -->
    <div class="sidebar-header border-bottom border-white border-opacity-10">
      <!-- แสดงเต็มบน desktop / เมื่อขยายบน mobile -->
      <div class="brand-full">
        <div class="brand-icon">
          <i class="bi bi-buildings"></i>
        </div>
        <div class="brand-text my-auto pe-3">
          <div class="fw-bold h4 mb-0">Central Fee</div>
          <small class="text-white-50">Management System</small>
        </div>
      </div>
      <!-- ปุ่ม toggle บน mobile -->
      <div class="mx-auto">
        <button
          class="toggle-btn d-lg-none"
          @click="toggleMobile"
          :title="mobileExpanded ? 'ย่อเมนู' : 'ขยายเมนู'"
        >
          <i :class="mobileExpanded ? 'bi bi-x-lg' : 'bi bi-list'"></i>
        </button>
      </div>
    </div>

    <!-- Menu Items -->
    <div class="sidebar-menu flex-grow-1 overflow-y-auto">
      <router-link
        v-for="item in visibleMenuItems"
        :key="item.label"
        :to="item.to"
        class="sidebar-link"
        :class="{ active: route.path.startsWith(item.to) }"
        @click="handleMenuClick"
      >
        <i :class="`bi ${item.icon} sidebar-icon`"></i>
        <span class="sidebar-label">{{ item.label }}</span>

        <!-- Tooltip บน mobile collapsed -->
        <span class="sidebar-tooltip">{{ item.label }}</span>
      </router-link>
    </div>

    <!-- User Footer -->
    <div class="sidebar-footer border-top border-white border-opacity-10">
      <div class="user-avatar">
        <i class="bi bi-person-circle mx-auto"></i>
      </div>
      <div class="user-info">
        <div class="fw-semibold small text-truncate">{{ auth.user?.name }}</div>
        <span class="role-badge">
          {{ roleLabelMap[auth.user?.role] ?? auth.user?.role }}
        </span>
      </div>
    </div>
  </aside>
</template>

<style scoped>
/* ─── Variables ─────────────────────────────────────── */
:root {
  --sidebar-width: 215px;
  --sidebar-collapsed: 60px;
}

/* ─── Overlay (mobile) ──────────────────────────────── */
.sidebar-overlay {
  position: fixed;
  inset: 0;
  background: rgba(0, 0, 0, 0.45);
  z-index: 99;
  backdrop-filter: blur(2px);
}

/* ─── Sidebar base ──────────────────────────────────── */
.sidebar {
  min-width: var(--sidebar-width) !important;
  min-height: 100vh;
  background: linear-gradient(180deg, #0d6efd 0%, #084298 100%);
  transition: width 0.25s ease;
  position: relative;
  z-index: 100;
  flex-shrink: 0;
}

/* ─── Header ────────────────────────────────────────── */
.sidebar-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 0.5rem;
  min-height: 72px;
}

.brand-full {
  display: flex;
  align-items: center;
  gap: 0.75rem;
  overflow: hidden;
}

.brand-collapsed {
  display: none;
}

.brand-icon {
  width: 40px;
  height: 40px;
  border-radius: 12px;
  background: rgba(255, 255, 255, 0.15);
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 1.15rem;
  flex-shrink: 0;
}

.brand-text {
  overflow: hidden;
  white-space: nowrap;
}

.toggle-btn {
  background: rgba(255, 255, 255, 0.12);
  border: none;
  color: #fff;
  width: 36px;
  height: 36px;
  border-radius: 10px;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  transition: background 0.15s;
  flex-shrink: 0;
}

.toggle-btn:hover {
  background: rgba(255, 255, 255, 0.22);
}

/* ─── Menu ──────────────────────────────────────────── */
.sidebar-menu {
  padding: 0.75rem;
}

.menu-section-title {
  font-size: 0.7rem;
  text-transform: uppercase;
  letter-spacing: 0.08em;
  color: rgba(255, 255, 255, 0.5);
  padding: 0 0.75rem;
  margin-bottom: 0.5rem;
  white-space: nowrap;
  overflow: hidden;
}

.sidebar-link {
  position: relative;
  display: flex;
  align-items: center;
  gap: 0.5rem;
  color: rgba(255, 255, 255, 0.88);
  text-decoration: none;
  padding: 0.5rem;
  border-radius: 0.875rem;
  margin-bottom: 0.25rem;
  transition: background 0.2s ease;
  white-space: nowrap;
  overflow: hidden;
}

.sidebar-link:hover {
  background: rgba(255, 255, 255, 0.12);
  color: #fff;
}

.sidebar-link.active {
  background: rgba(255, 255, 255, 0.2);
  color: #fff;
  font-weight: 600;
}

.sidebar-icon {
  font-size: 1.1rem;
  flex-shrink: 0;
  width: 20px;
  text-align: center;
}

.sidebar-label {
  overflow: hidden;
  text-overflow: ellipsis;
}

/* Tooltip (แสดงเมื่อ collapsed บน mobile) */
.sidebar-tooltip {
  display: none;
  position: absolute;
  left: calc(var(--sidebar-collapsed) + 4px);
  top: 50%;
  transform: translateY(-50%);
  background: #084298;
  color: #fff;
  font-size: 0.8rem;
  padding: 0.35rem 0.75rem;
  border-radius: 0.5rem;
  white-space: nowrap;
  pointer-events: none;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.25);
  z-index: 200;
}

/* ─── Footer ────────────────────────────────────────── */
.sidebar-footer {
  display: flex;
  align-items: center;
  gap: 0.75rem;
  padding: 0.875rem 1rem;
  overflow: hidden;
}

.user-avatar {
  font-size: 1.4rem;
  color: rgba(255, 255, 255, 0.6);
  flex-shrink: 0;
}

.user-info {
  overflow: hidden;
  white-space: nowrap;
}

.role-badge {
  display: inline-block;
  font-size: 0.68rem;
  padding: 0.15rem 0.55rem;
  border-radius: 999px;
  background: rgba(255, 255, 255, 0.15);
  color: rgba(255, 255, 255, 0.8);
  border: 1px solid rgba(255, 255, 255, 0.2);
}

/* ─── MOBILE: collapsed (icon only) ────────────────── */
@media (max-width: 991.98px) {
  .sidebar {
    min-width: var(--sidebar-collapsed) !important;
  }

  /* ซ่อน brand text และ section title */
  .brand-full {
    display: none;
  }

  .brand-collapsed {
    display: flex;
  }

  .menu-section-title {
    visibility: hidden;
    height: 1rem;
  }

  /* ซ่อน label text */
  .sidebar-label {
    display: none;
  }

  /* ซ่อน user info */
  .user-info {
    display: none;
  }

  /* center icon ใน link */
  .sidebar-link {
    justify-content: center;
    padding: 0.8rem;
  }

  /* แสดง tooltip เมื่อ hover */
  .sidebar-link:hover .sidebar-tooltip {
    display: block;
  }

  /* sidebar ขยายเมื่อ toggle */
  .sidebar.mobile-expanded {
    width: var(--sidebar-width);
    position: fixed;
    top: 0;
    left: 0;
    height: 100vh;
    box-shadow: 4px 0 24px rgba(0, 0, 0, 0.25);
  }

  .sidebar.mobile-expanded .brand-full {
    display: flex;
  }

  .sidebar.mobile-expanded .brand-collapsed {
    display: none;
  }

  .sidebar.mobile-expanded .menu-section-title {
    visibility: visible;
  }

  .sidebar.mobile-expanded .sidebar-label {
    display: block;
  }

  .sidebar.mobile-expanded .user-info {
    display: block;
  }

  .sidebar.mobile-expanded .sidebar-link {
    justify-content: flex-start;
    padding: 0.8rem 0.75rem;
  }

  .sidebar.mobile-expanded .sidebar-link:hover .sidebar-tooltip {
    display: none;
  }
}

/* ─── DESKTOP: always full width ───────────────────── */
@media (min-width: 992px) {
  .sidebar {
    min-width: var(--sidebar-width) !important;
  }

  .toggle-btn {
    display: none;
  }
}
</style>
